import React, {useState} from "react"
import {MutationFn} from "@irati/utils/apollo"

import {Poll} from "@irati/types"
import {ApolloState} from "@irati/utils/services/apollo/apolloStateInterface"
import Validation, {validatedScreen} from "@irati/utils/validation"
import {FormattedMessage, InjectIntl, injectIntl} from "@irati/utils/lang"
import {CompanyValuesSelect} from "@irati-web/fields/companyValues"
import getId from "@irati/utils/id"
import {Title, Desc, EndDate, Share} from "@irati-web/fields/poll"
import Trophy from "@irati-web/fields/trophy"
import tracking from "@irati-tracking/recognition/poll"
import {EVERYONE_OPTION, SPECIFIC_TEAMS_OPTION, SPECIFIC_TEAMS} from "@irati/recognition/constants/share"
import {H3} from "@irati/ui/src/Typography/Heading"
import Flex from "@irati/ui/src/Flex"

import authentication from "@irati/auth"

import ResultsVisibility from "@irati-web/fields/poll/ResultsVisibility"
import {RESULTS_VISIBILITY_PROPERTY} from "@irati/recognition/constants/resultsVisibility"
import {Checkbox} from "@irati/ui/src/FormItem"
import {Body} from "@irati/ui/src/Typography"
import PublishCustomization from "@irati-web/fields/poll/PublishCustomization"
import {PUBLISH_CUSTOMIZATION_PROPERTY} from "@irati/recognition/constants/publishCustomization"
import TeamSearch from "@irati/ui/src/TeamSearch"
import CTA from "../shared/CTA"
import {HeaderContainer, StyledForm, StyledHr} from "./styled/Create"
import InspireMe from "./shared/InspireMe"
import DateWarning from "./shared/DateWarning"
import {DATE_PICKER, INSPIRE} from "./Popup"

interface Props extends InjectIntl {
  poll: Poll
  apolloState: ApolloState
  onSubmit: MutationFn
  onChange: (poll: Poll, property: string, value: string) => void
  onSharingOptionChange: (share: string) => void
  selectScreen: (modalToShow: string | null) => void
  share: string
  focusReward?: boolean
  id?: string
}

const CreatePoll = (props: Props) => {
  const {poll, share, onChange, apolloState, formatMessage, focusReward, onSubmit, selectScreen, onSharingOptionChange} = props
  const isAdmin = authentication.isGlobalAdmin()
  const [submit, setSubmit] = useState<Promise<any>>()
  const [trophyValidationError, setTrophyValidationError] = useState(false)

  const handleNext = () => {
    if (!poll.trophy) {
      return setTrophyValidationError(true)
    }
    const submitRes = onSubmit()
    setSubmit(submitRes)
    return submitRes
  }

  const handleCancel = () => {
    tracking.cancelPoll()
    selectScreen(null)
  }

  const handleTrophySelect = (trophy: string) => {
    onChange(poll, "trophy", trophy)
  }

  const handleCustomDate = () => {
    selectScreen(DATE_PICKER)
  }

  const handleInspire = () => {
    tracking.viewInspirations()
    selectScreen(INSPIRE)
  }

  const handleShareToggle = (currentValue, key, {value}) => {
    onSharingOptionChange(value)
  }

  const handleResultsVisibilityToggle = ({value}) => {
    onChange(poll, RESULTS_VISIBILITY_PROPERTY, value)
  }

  const handleAnonymousToggle = () => {
    onChange(poll, "allowAnonymousVoting", !poll.allowAnonymousVoting)
  }

  const handlePublishCustomizationToggle = ({value}) => {
    onChange(poll, PUBLISH_CUSTOMIZATION_PROPERTY, value)
  }

  const schema = Validation.array("platform.defaultValidation.required").of(Validation.string("platform.defaultValidation.required"))

  return (
    <>
      <HeaderContainer>
        <Flex alignItems="center">
          <H3 marginRight={1}>
            <FormattedMessage id="recognition.popup.createPoll.create.header" />
          </H3>
          <InspireMe onClick={handleInspire} />
        </Flex>
      </HeaderContainer>
      <StyledHr />
      <StyledForm>
        <Title autoFocus={!focusReward} poll={poll} onChange={onChange} />
        <Trophy componentToHideOnExpand={() => <Desc poll={poll} onChange={onChange} />} item={poll} onChange={handleTrophySelect} />
        <CompanyValuesSelect item={poll} onChange={onChange} />
        <EndDate poll={poll} onChange={onChange} onCustomDate={handleCustomDate} />
        <DateWarning poll={poll} />
        <Share
          options={[EVERYONE_OPTION(formatMessage), SPECIFIC_TEAMS_OPTION(formatMessage)]}
          poll={{share}}
          onChange={handleShareToggle}
        />
        {isAdmin && <ResultsVisibility value={poll.resultVisibility} onChange={handleResultsVisibilityToggle} />}
        {isAdmin && <PublishCustomization value={poll.publishType} onChange={handlePublishCustomizationToggle} />}
        {share === SPECIFIC_TEAMS && (
          <TeamSearch
            includeAllTeams={false}
            isMulti
            label="recognition.fields.poll.teams.label"
            placeholder="recognition.fields.poll.teams.placeholder"
            validation={schema}
            onChange={(teams) => {
              onChange(poll, "teams", teams?.map(({value}) => value) || [])
            }}
          />
        )}
        {isAdmin && (
          <Checkbox checked={poll.allowAnonymousVoting} name="isAnonymous" onChange={handleAnonymousToggle}>
            <Body small display="inline">
              <FormattedMessage id="recognition.popup.createPoll.anonymous" />
            </Body>
            <Body small weight="normal">
              <FormattedMessage id="recognition.popup.createPoll.anonymousHint" />
            </Body>
          </Checkbox>
        )}
      </StyledForm>
      <CTA
        id={getId("submitButton", props)}
        onNext={handleNext}
        onCancel={handleCancel}
        load={submit}
        apolloState={apolloState}
        trophyValidationError={trophyValidationError}
      />
    </>
  )
}
CreatePoll.defaultProps = {
  focusReward: false,
  id: "createPoll"
}
export default injectIntl(validatedScreen(CreatePoll, "CreatePoll"))
